import type { CoverPicture } from './drawBackground';

const SAMPLE_SIDE = 32;
const toHex = (value: number): string => Math.round(value).toString(16).padStart(2, '0');

/**
 * The main colours of a picture from its pixels (RGBA bytes): the pixels go into coarse colour
 * cells, and the fullest cells that are not near a colour already taken give up to `count`
 * colours (`#rrggbb`, the mean of each cell). Pure.
 */
export function paletteFromPixels(data: ArrayLike<number>, count = 5): string[] {
  const cells = new Map<number, { red: number; green: number; blue: number; pixels: number }>();
  for (let index = 0; index + 3 < data.length; index += 4) {
    const red = data[index] ?? 0;
    const green = data[index + 1] ?? 0;
    const blue = data[index + 2] ?? 0;
    if ((data[index + 3] ?? 255) >= 128) {
      // 16 steps per channel.
      const key = Math.floor(red / 16) * 256 + Math.floor(green / 16) * 16 + Math.floor(blue / 16);
      const cell = cells.get(key) ?? { red: 0, green: 0, blue: 0, pixels: 0 };
      cell.red += red;
      cell.green += green;
      cell.blue += blue;
      cell.pixels += 1;
      cells.set(key, cell);
    }
  }
  const means = [...cells.values()]
    .sort((a, b) => b.pixels - a.pixels)
    .map((cell) => [cell.red / cell.pixels, cell.green / cell.pixels, cell.blue / cell.pixels] as const);
  const picked: Array<readonly [number, number, number]> = [];
  for (const colour of means) {
    const isNear = picked.some(
      (other) => Math.hypot(colour[0] - other[0], colour[1] - other[1], colour[2] - other[2]) < 48
    );
    if (!isNear) {
      picked.push(colour);
    }
    if (picked.length >= count) {
      break;
    }
  }
  return picked.map(([red, green, blue]) => `#${toHex(red)}${toHex(green)}${toHex(blue)}`);
}

/**
 * The 5 colours of an uploaded picture, read in the browser (an own picture has no palette from
 * the API). Empty when the pixels cannot be read.
 */
export function paletteFromPicture(picture: CoverPicture): string[] {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = SAMPLE_SIDE;
    canvas.height = SAMPLE_SIDE;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx === null) {
      return [];
    }
    ctx.drawImage(picture.source, 0, 0, SAMPLE_SIDE, SAMPLE_SIDE);
    return paletteFromPixels(ctx.getImageData(0, 0, SAMPLE_SIDE, SAMPLE_SIDE).data);
  } catch (error) {
    console.warn('Cover editor: the colours of the picture cannot be read', error);
    return [];
  }
}
